export type AnyUser = {
  role?: unknown;
  user_type?: unknown;
  roles?: unknown;
} | null | undefined;

const KNOWN_ROLES = new Set([
  'super_admin',
  'admin',
  'manager',
  'staff',
  'organization_admin',
  'organization_manager',
  'organizer',
  'scanner',
  'jury',
  'participant',
]);

/** Higher index means lower priority — matches backend UserRoleResolver. */
const ROLE_PRIORITY = [
  'super_admin',
  'admin',
  'manager',
  'staff',
  'organization_admin',
  'organization_manager',
  'organizer',
  'scanner',
  'jury',
  'judge',
  'participant',
] as const;

function collectRoleNames(user: NonNullable<AnyUser>): string[] {
  const rawRoles = Array.isArray(user.roles) ? user.roles : [];
  return rawRoles
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object' && 'name' in entry) {
        return String((entry as { name?: unknown }).name ?? '');
      }
      return '';
    })
    .map((role) => normalizeRole(role))
    .filter((role) => role !== '');
}

export function normalizeRole(value: unknown): string {
  const role = String(value ?? '').trim().toLowerCase();
  if (!role) return 'participant';

  if (role === 'superadmin') return 'super_admin';
  if (role === 'user' || role === 'attendee' || role === 'network_user' || role === 'individual') {
    return 'participant';
  }
  if (role === 'organization') return 'organization_admin';
  if (role === 'individual_organizer') return 'organizer';
  if (role === 'judge') return 'jury';
  return role;
}

function presentRole(value: unknown): string {
  if (value == null || String(value).trim() === '') return '';
  return normalizeRole(value);
}

/**
 * Resolve the dashboard role the same way production auth does:
 * prefer a specific users.user_type, otherwise the highest Spatie/API role.
 * Legacy `individual` rows must not hide jury/organizer assignments.
 */
export function getEffectiveRole(user: AnyUser): string {
  if (!user) return 'participant';

  const userType = presentRole(user.user_type);
  const namedRole = presentRole(user.role);
  const roles = collectRoleNames(user);

  if (userType && userType !== 'participant' && KNOWN_ROLES.has(userType)) {
    return userType;
  }

  const candidates = [namedRole, ...roles].filter((role) => role !== '');
  const specific = candidates.filter((role) => role !== 'participant');
  if (specific.length > 0) {
    for (const priorityRole of ROLE_PRIORITY) {
      const normalizedPriority = normalizeRole(priorityRole);
      if (normalizedPriority === 'participant') continue;
      if (specific.includes(normalizedPriority)) {
        return normalizedPriority;
      }
    }
    return specific[0];
  }

  return userType || namedRole || 'participant';
}

export function isAdminRole(role: unknown): boolean {
  const normalized = normalizeRole(role);
  return ['admin', 'super_admin', 'staff', 'manager'].includes(normalized);
}

export function isOrganizationAdminRole(role: unknown): boolean {
  return normalizeRole(role) === 'organization_admin';
}

export function isOrganizationManagerRole(role: unknown): boolean {
  return normalizeRole(role) === 'organization_manager';
}

export function isOrganizationStaffRole(role: unknown): boolean {
  const normalized = normalizeRole(role);
  return normalized === 'organization_admin' || normalized === 'organization_manager';
}

export function isOrganizerRole(role: unknown): boolean {
  const normalized = normalizeRole(role);
  return ['organizer', 'individual_organizer'].includes(normalized);
}

export function isJuryRole(role: unknown): boolean {
  return normalizeRole(role) === 'jury';
}

export function isParticipantRole(role: unknown): boolean {
  return (
    !isAdminRole(role) &&
    !isOrganizationAdminRole(role) &&
    !isOrganizerRole(role) &&
    !isJuryRole(role)
  );
}

/** Individual participants who still need organizer onboarding. */
export function canBecomeOrganizer(user: AnyUser): boolean {
  if (!user) return false;

  const role = getEffectiveRole(user);
  if (isAdminRole(role)) return false;
  if (isOrganizerRole(role)) return false;
  if (isOrganizationStaffRole(role)) return false;
  if (isJuryRole(role)) return false;
  if (normalizeRole(role) === 'scanner') return false;

  return true;
}
